/**
 * HandGestureRecognizer — W3C XR Hand Input gesture detection with state machine.
 *
 * Processes 25-joint hand data from the WebXR Hand Input API to detect 9 built-in
 * gestures plus user-registered custom gestures. Each gesture runs an independent
 * two-threshold state machine (0.8 to start, 0.5 to sustain) to prevent flickering.
 *
 * Usage:
 *   const recognizer = new HandGestureRecognizer();
 *   // In XR frame loop:
 *   recognizer.update(leftHandJoints, rightHandJoints, inputReader);
 *   // Query:
 *   const pinch = recognizer.getGesture(0, GestureType.Pinch);
 */

import { GestureType, GestureState, type GestureEvent } from './types.js';
import type { InputReader } from './InputReader.js';

// ─── Joint Index Constants ────────────────────────────────────────────────────
//
// W3C XR Hand Input Module — 25 joints per hand (only fingertip/wrist indices
// are used by the built-in detectors; intermediate phalanx indices are documented
// here for reference when authoring custom detectors).

/** Wrist base joint (index 0). Serves as the reference point for curl/extension tests. */
const WRIST      = 0;

/** Thumb fingertip (index 4). */
const THUMB_TIP  = 4;

/** Index fingertip (index 9). */
const INDEX_TIP  = 9;

/** Middle fingertip (index 14). */
const MIDDLE_TIP = 14;

/** Ring fingertip (index 19). */
const RING_TIP   = 19;

/** Pinky fingertip (index 24). */
const PINKY_TIP  = 24;

// ─── State Machine Thresholds ────────────────────────────────────────────────

/** Minimum confidence to START a gesture (None → Started). */
const CONFIDENCE_START = 0.8;

/** Minimum confidence to HOLD a gesture (Started/Held → Held). */
const CONFIDENCE_HOLD  = 0.5;

// ─── Gesture → Action Name Map ───────────────────────────────────────────────

/** Action name fragments used to derive XR_<Gesture>_<Hand> button action strings. */
const GESTURE_ACTION_NAMES: Record<number, string> = {
  [GestureType.Pinch]:     'Pinch',
  [GestureType.Grab]:      'Grab',
  [GestureType.Point]:     'Point',
  [GestureType.OpenPalm]:  'OpenPalm',
  [GestureType.ThumbsUp]:  'ThumbsUp',
  [GestureType.ThumbsDown]:'ThumbsDown',
  [GestureType.Peace]:     'Peace',
  [GestureType.FingerGun]: 'FingerGun',
  [GestureType.Rock]:      'Rock',
};

// ─── Public Types ─────────────────────────────────────────────────────────────

/** Minimal joint pose — only position is required for gesture detection. */
export type JointPose = { position: { x: number; y: number; z: number } };

/**
 * A gesture detector function. Receives the 25 joint poses for one hand and
 * returns a detected flag plus a confidence value in [0, 1].
 *
 * Note: detectors SHOULD return fresh objects. The recognizer copies the
 * result into its own scratch before running the next detector, so your
 * detector cannot safely call another detector and read its return — use
 * the raw joint data instead.
 */
export type GestureDetectorFn = (joints: JointPose[]) => { detected: boolean; confidence: number };

// ─── Internal Types ───────────────────────────────────────────────────────────

interface RegisteredDetector {
  type: GestureType;
  name: string;
  fn: GestureDetectorFn;
}

/**
 * Per-hand, per-gesture state machine cell.
 * Implements GestureEvent so the same object can be handed directly to
 * InputReader._setGesture — avoids allocating a wrapper object each frame.
 */
interface GestureCell {
  type: GestureType;
  hand: 0 | 1;
  state: GestureState;
  confidence: number;
}

// ─── Distance Helper ─────────────────────────────────────────────────────────

/**
 * Euclidean distance between two 3D positions.
 */
function distance(
  a: { x: number; y: number; z: number },
  b: { x: number; y: number; z: number },
): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  const d2 = dx * dx + dy * dy + dz * dz;
  // WebXR Hand Input returns NaN for untracked joints. Math.sqrt(NaN) = NaN,
  // which then poisons every threshold comparison (`NaN < x` is false), silently
  // locking the gesture state machine. Return Infinity so every proximity
  // detector reports "not close" for NaN inputs and downstream state transitions
  // return cleanly to idle.
  if (!Number.isFinite(d2)) return Infinity;
  return Math.sqrt(d2);
}

/** Clamp a number to [min, max]. */
function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

// ─── Built-in Gesture Detectors ───────────────────────────────────────────────
//
// All built-ins return a fresh object per call. This costs one small
// allocation per detector per hand per frame (~18 allocs at 90 Hz stereo),
// in exchange for composition safety: the previous design shared a module-
// level scratch, which silently corrupted results if a custom detector
// called any built-in internally.

function detectPinch(joints: JointPose[]): { detected: boolean; confidence: number } {
  const dist = distance(joints[THUMB_TIP].position, joints[INDEX_TIP].position);
  return { detected: dist < 0.02, confidence: clamp(1 - dist / 0.04, 0, 1) };
}

function detectGrab(joints: JointPose[]): { detected: boolean; confidence: number } {
  // Indexed access instead of building a temporary `tips` array — the old
  // code allocated a 5-element array every call (×2 hands ×90 Hz = 900/sec).
  const wrist = joints[WRIST].position;
  const dThumb  = distance(joints[THUMB_TIP].position,  wrist);
  const dIndex  = distance(joints[INDEX_TIP].position,  wrist);
  const dMiddle = distance(joints[MIDDLE_TIP].position, wrist);
  const dRing   = distance(joints[RING_TIP].position,   wrist);
  const dPinky  = distance(joints[PINKY_TIP].position,  wrist);
  const allClose = dThumb < 0.04 && dIndex < 0.04 && dMiddle < 0.04 && dRing < 0.04 && dPinky < 0.04;
  const avgDist = (dThumb + dIndex + dMiddle + dRing + dPinky) / 5;
  return { detected: allClose, confidence: clamp(1 - avgDist / 0.08, 0, 1) };
}

function detectPoint(joints: JointPose[]): { detected: boolean; confidence: number } {
  const wrist = joints[WRIST].position;
  const indexDist  = distance(joints[INDEX_TIP].position, wrist);
  const middleDist = distance(joints[MIDDLE_TIP].position, wrist);
  const ringDist   = distance(joints[RING_TIP].position, wrist);
  const pinkyDist  = distance(joints[PINKY_TIP].position, wrist);

  const indexExtended = indexDist > 0.08;
  const othersCurled  = middleDist < 0.05 && ringDist < 0.05 && pinkyDist < 0.05;

  const extensionScore = clamp((indexDist - 0.04) / 0.08, 0, 1);
  const curlScore      = clamp(1 - (middleDist + ringDist + pinkyDist) / (3 * 0.1), 0, 1);
  return {
    detected: indexExtended && othersCurled,
    confidence: clamp((extensionScore + curlScore) / 2, 0, 1),
  };
}

function detectOpenPalm(joints: JointPose[]): { detected: boolean; confidence: number } {
  const wrist = joints[WRIST].position;
  const dThumb  = distance(joints[THUMB_TIP].position,  wrist);
  const dIndex  = distance(joints[INDEX_TIP].position,  wrist);
  const dMiddle = distance(joints[MIDDLE_TIP].position, wrist);
  const dRing   = distance(joints[RING_TIP].position,   wrist);
  const dPinky  = distance(joints[PINKY_TIP].position,  wrist);
  const allExtended =
    dThumb >= 0.08 && dIndex >= 0.08 && dMiddle >= 0.08 && dRing >= 0.08 && dPinky >= 0.08;
  const totalScore =
    clamp((dThumb  - 0.04) / 0.08, 0, 1) +
    clamp((dIndex  - 0.04) / 0.08, 0, 1) +
    clamp((dMiddle - 0.04) / 0.08, 0, 1) +
    clamp((dRing   - 0.04) / 0.08, 0, 1) +
    clamp((dPinky  - 0.04) / 0.08, 0, 1);
  return { detected: allExtended, confidence: clamp(totalScore / 5, 0, 1) };
}

function detectThumbsUp(joints: JointPose[]): { detected: boolean; confidence: number } {
  const thumbTip = joints[THUMB_TIP].position;
  const wrist    = joints[WRIST].position;
  const thumbAbove = thumbTip.y > wrist.y;

  const indexTip  = joints[INDEX_TIP].position;
  const middleTip = joints[MIDDLE_TIP].position;
  const ringTip   = joints[RING_TIP].position;
  const pinkyTip  = joints[PINKY_TIP].position;
  const dIndex  = distance(indexTip,  wrist);
  const dMiddle = distance(middleTip, wrist);
  const dRing   = distance(ringTip,   wrist);
  const dPinky  = distance(pinkyTip,  wrist);

  const fingersCurled = dIndex < 0.06 && dMiddle < 0.06 && dRing < 0.06 && dPinky < 0.06;
  const curlSum =
    clamp(1 - dIndex  / 0.08, 0, 1) +
    clamp(1 - dMiddle / 0.08, 0, 1) +
    clamp(1 - dRing   / 0.08, 0, 1) +
    clamp(1 - dPinky  / 0.08, 0, 1);
  const curlScore = curlSum / 4;

  const thumbIsHighest =
    indexTip.y < thumbTip.y && middleTip.y < thumbTip.y && ringTip.y < thumbTip.y && pinkyTip.y < thumbTip.y;

  const heightScore = clamp((thumbTip.y - wrist.y) / 0.1, 0, 1);
  return {
    detected: thumbAbove && fingersCurled && thumbIsHighest,
    confidence: clamp((curlScore + heightScore) / 2, 0, 1),
  };
}

function detectThumbsDown(joints: JointPose[]): { detected: boolean; confidence: number } {
  const thumbTip = joints[THUMB_TIP].position;
  const wrist    = joints[WRIST].position;
  const thumbBelow = thumbTip.y < wrist.y;

  const indexTip  = joints[INDEX_TIP].position;
  const middleTip = joints[MIDDLE_TIP].position;
  const ringTip   = joints[RING_TIP].position;
  const pinkyTip  = joints[PINKY_TIP].position;
  const dIndex  = distance(indexTip,  wrist);
  const dMiddle = distance(middleTip, wrist);
  const dRing   = distance(ringTip,   wrist);
  const dPinky  = distance(pinkyTip,  wrist);

  const fingersCurled = dIndex < 0.06 && dMiddle < 0.06 && dRing < 0.06 && dPinky < 0.06;
  const curlSum =
    clamp(1 - dIndex  / 0.08, 0, 1) +
    clamp(1 - dMiddle / 0.08, 0, 1) +
    clamp(1 - dRing   / 0.08, 0, 1) +
    clamp(1 - dPinky  / 0.08, 0, 1);
  const curlScore = curlSum / 4;

  const thumbIsLowest =
    indexTip.y > thumbTip.y && middleTip.y > thumbTip.y && ringTip.y > thumbTip.y && pinkyTip.y > thumbTip.y;

  const depthScore = clamp((wrist.y - thumbTip.y) / 0.1, 0, 1);
  return {
    detected: thumbBelow && fingersCurled && thumbIsLowest,
    confidence: clamp((curlScore + depthScore) / 2, 0, 1),
  };
}

function detectPeace(joints: JointPose[]): { detected: boolean; confidence: number } {
  const wrist = joints[WRIST].position;
  const indexDist  = distance(joints[INDEX_TIP].position, wrist);
  const middleDist = distance(joints[MIDDLE_TIP].position, wrist);
  const ringDist   = distance(joints[RING_TIP].position, wrist);
  const pinkyDist  = distance(joints[PINKY_TIP].position, wrist);

  const detected =
    indexDist > 0.08 && middleDist > 0.08 && ringDist < 0.05 && pinkyDist < 0.05;

  const extScore  = clamp(((indexDist + middleDist) / 2 - 0.04) / 0.08, 0, 1);
  const curlScore = clamp(1 - (ringDist + pinkyDist) / (2 * 0.1), 0, 1);
  return { detected, confidence: clamp((extScore + curlScore) / 2, 0, 1) };
}

function detectFingerGun(joints: JointPose[]): { detected: boolean; confidence: number } {
  const wrist    = joints[WRIST].position;
  const thumbTip = joints[THUMB_TIP].position;

  const indexDist  = distance(joints[INDEX_TIP].position, wrist);
  const middleDist = distance(joints[MIDDLE_TIP].position, wrist);
  const ringDist   = distance(joints[RING_TIP].position, wrist);
  const pinkyDist  = distance(joints[PINKY_TIP].position, wrist);
  const thumbDist  = distance(thumbTip, wrist);

  const detected =
    indexDist > 0.08 &&
    thumbDist > 0.06 && thumbTip.y > wrist.y &&
    middleDist < 0.05 && ringDist < 0.05 && pinkyDist < 0.05;

  const extScore  = clamp(((indexDist + thumbDist) / 2 - 0.04) / 0.08, 0, 1);
  const curlScore = clamp(1 - (middleDist + ringDist + pinkyDist) / (3 * 0.1), 0, 1);
  return { detected, confidence: clamp((extScore + curlScore) / 2, 0, 1) };
}

function detectRock(joints: JointPose[]): { detected: boolean; confidence: number } {
  const wrist = joints[WRIST].position;
  const indexDist  = distance(joints[INDEX_TIP].position, wrist);
  const middleDist = distance(joints[MIDDLE_TIP].position, wrist);
  const ringDist   = distance(joints[RING_TIP].position, wrist);
  const pinkyDist  = distance(joints[PINKY_TIP].position, wrist);

  const detected =
    indexDist > 0.08 && pinkyDist > 0.08 && middleDist < 0.05 && ringDist < 0.05;

  const extScore  = clamp(((indexDist + pinkyDist) / 2 - 0.04) / 0.08, 0, 1);
  const curlScore = clamp(1 - (middleDist + ringDist) / (2 * 0.1), 0, 1);
  return { detected, confidence: clamp((extScore + curlScore) / 2, 0, 1) };
}

// ─── HandGestureRecognizer ────────────────────────────────────────────────────

export class HandGestureRecognizer {
  private _detectors: RegisteredDetector[] = [];

  private _states: [Map<number, GestureCell>, Map<number, GestureCell>] = [
    new Map(),
    new Map(),
  ];

  /**
   * Cache of `XR_<Name>_<Hand>` action strings. Populated once per
   * registered gesture to avoid rebuilding a template literal every time a
   * state machine transitions (up to 2 hands × N detectors per frame in XR).
   */
  private _actionNameCache = new Map<number, string>();

  constructor() {
    this._registerBuiltins();
  }

  update(
    leftJoints: JointPose[] | null,
    rightJoints: JointPose[] | null,
    reader: InputReader,
  ): void {
    if (leftJoints !== null)  this._processHand(0, leftJoints,  reader);
    if (rightJoints !== null) this._processHand(1, rightJoints, reader);
  }

  getGesture(hand: 0 | 1, type: GestureType): GestureEvent | undefined {
    const cell = this._states[hand].get(type);
    return cell as GestureEvent | undefined;
  }

  getGestureConfidence(hand: 0 | 1, type: GestureType): number {
    return this._states[hand].get(type)?.confidence ?? 0;
  }

  /**
   * Register a custom gesture detector. Overrides any existing detector for
   * the same GestureType.
   */
  registerGesture(name: string, type: GestureType, detector: GestureDetectorFn): void {
    const existing = this._detectors.findIndex(d => d.type === type);
    const entry: RegisteredDetector = { type, name, fn: detector };
    if (existing !== -1) {
      this._detectors[existing] = entry;
    } else {
      this._detectors.push(entry);
    }
    // Warm the action-name cache so _processHand never allocates a new
    // template literal for this gesture's press/release events.
    GESTURE_ACTION_NAMES[type] = name;
    this._cacheActionNames(type, name);
  }

  getActiveGestures(hand: 0 | 1): GestureEvent[] {
    const result: GestureEvent[] = [];
    for (const [type, cell] of this._states[hand]) {
      if (cell.state !== GestureState.None) {
        result.push({
          type:       type as GestureType,
          state:      cell.state,
          hand,
          confidence: cell.confidence,
        });
      }
    }
    return result;
  }

  dispose(): void {
    this._detectors = [];
    this._states[0].clear();
    this._states[1].clear();
    this._actionNameCache.clear();
  }

  // ─── Private Helpers ──────────────────────────────────────────────

  private _registerBuiltins(): void {
    this._detectors.push(
      { type: GestureType.Pinch,      name: 'Pinch',      fn: detectPinch      },
      { type: GestureType.Grab,       name: 'Grab',       fn: detectGrab       },
      { type: GestureType.Point,      name: 'Point',      fn: detectPoint      },
      { type: GestureType.OpenPalm,   name: 'OpenPalm',   fn: detectOpenPalm   },
      { type: GestureType.ThumbsUp,   name: 'ThumbsUp',   fn: detectThumbsUp   },
      { type: GestureType.ThumbsDown, name: 'ThumbsDown', fn: detectThumbsDown },
      { type: GestureType.Peace,      name: 'Peace',      fn: detectPeace      },
      { type: GestureType.FingerGun,  name: 'FingerGun',  fn: detectFingerGun  },
      { type: GestureType.Rock,       name: 'Rock',       fn: detectRock       },
    );
    // Warm the cache for the 9 built-ins so the first-frame dispatch doesn't
    // have to fall back to the on-demand build path.
    for (const det of this._detectors) {
      this._cacheActionNames(det.type, det.name);
    }
  }

  private _cacheActionNames(type: GestureType, name: string): void {
    // Keys: type*2 + hand (hand ∈ {0,1}).
    this._actionNameCache.set(type * 2 + 0, `XR_${name}_Left`);
    this._actionNameCache.set(type * 2 + 1, `XR_${name}_Right`);
  }

  private _processHand(hand: 0 | 1, joints: JointPose[], reader: InputReader): void {
    const stateMap = this._states[hand];

    for (const detector of this._detectors) {
      const result = detector.fn(joints);
      const { type } = detector;

      let cell = stateMap.get(type);
      if (!cell) {
        cell = { type, hand, state: GestureState.None, confidence: 0 };
        stateMap.set(type, cell);
      }

      const prevState = cell.state;
      let nextState   = prevState;

      switch (prevState) {
        case GestureState.None:
          if (result.detected && result.confidence >= CONFIDENCE_START) {
            nextState = GestureState.Started;
          }
          break;

        case GestureState.Started:
          if (result.detected && result.confidence >= CONFIDENCE_HOLD) {
            nextState = GestureState.Held;
          } else {
            nextState = GestureState.Ended;
          }
          break;

        case GestureState.Held:
          if (!result.detected || result.confidence < CONFIDENCE_HOLD) {
            nextState = GestureState.Ended;
          }
          break;

        case GestureState.Ended:
          // Transition Ended → None on the FOLLOWING frame, not in the
          // same call. Without this, code that polls `getGesture()` after
          // `update()` returns sees `None` immediately and never observes
          // the `Ended` state — only event subscribers see it. Matches
          // Unity-style input API contract: Ended persists for one full
          // frame so polling code can detect release.
          if (result.detected && result.confidence >= CONFIDENCE_START) {
            // Re-detected — start a new gesture cycle.
            nextState = GestureState.Started;
          } else {
            nextState = GestureState.None;
          }
          break;
      }

      cell.state      = nextState;
      cell.confidence = result.confidence;
      reader._setGesture(cell);

      // Edge events use the precomputed action-name cache instead of
      // rebuilding a template literal on every transition.
      // Fire the press edge for BOTH transitions into Started: the
      // state machine can go directly Ended→Started on re-detection
      // (see the `Ended` case above), bypassing None. The prior cycle
      // already fired `_setReleased` on entering Ended, so without
      // handling the Ended→Started edge the action would stay absent
      // from the reader's `held` set for the entire resumed gesture
      // (a momentary 90Hz confidence dip below CONFIDENCE_HOLD then
      // recovery above CONFIDENCE_START would silently drop the press).
      if (
        nextState === GestureState.Started &&
        (prevState === GestureState.None || prevState === GestureState.Ended)
      ) {
        const actionName = this._actionNameCache.get(type * 2 + hand);
        if (actionName) reader._setPressed(actionName);
      } else if (nextState === GestureState.Ended) {
        const actionName = this._actionNameCache.get(type * 2 + hand);
        if (actionName) reader._setReleased(actionName);
      }
    }
  }

  forceReleaseHand(hand: 0 | 1, reader: InputReader): void {
    const stateMap = this._states[hand];
    for (const [type, cell] of stateMap) {
      if (cell.state === GestureState.Started || cell.state === GestureState.Held) {
        cell.state = GestureState.Ended;
        cell.confidence = 0;
        reader._setGesture(cell);
        const actionName = this._actionNameCache.get(type * 2 + hand);
        if (actionName) reader._setReleased(actionName);
        cell.state = GestureState.None;
        reader._setGesture(cell);
      } else if (cell.state === GestureState.Ended) {
        cell.state = GestureState.None;
        cell.confidence = 0;
        reader._setGesture(cell);
      }
    }
  }
}
